
import { useCallback, useEffect, useRef } from 'react';
import { useBeforeUnload, useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook to show a confirmation dialog when navigating away with unsaved changes
 * 
 * @param hasUnsavedChanges Boolean indicating if there are unsaved changes
 * @param message Custom message to show (optional)
 */
export function useUnsavedChangesPrompt(
  hasUnsavedChanges: boolean,
  message: string = 'You have unsaved changes. Are you sure you want to leave?'
) {
  const navigate = useNavigate();
  const location = useLocation();
  const lastLocationRef = useRef(location.pathname);
  
  // Add beforeunload event listener for browser/tab close
  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          return message;
        }
      },
      [hasUnsavedChanges, message]
    )
  );
  
  // This effect handles navigation within the React app with reduced rerenders
  useEffect(() => {
    // Skip processing if location hasn't actually changed
    if (lastLocationRef.current === location.pathname) {
      return;
    }
    
    lastLocationRef.current = location.pathname;
    
    if (!hasUnsavedChanges) return;
    
    // Create function to handle navigation events
    const handleBeforeNavigate = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        if (!window.confirm(message)) {
          event.preventDefault();
        }
      }
    };
    
    // Add event listener
    window.addEventListener('beforeunload', handleBeforeNavigate);
    
    // Clean up
    return () => {
      window.removeEventListener('beforeunload', handleBeforeNavigate);
    };
  }, [hasUnsavedChanges, message, location.pathname]);
  
  // Helper function to use when programmatically navigating
  const promptIfUnsaved = useCallback(
    (navigateTo: string) => {
      // Skip prompt if we're already on this path
      if (navigateTo === location.pathname) {
        return;
      }
      
      if (hasUnsavedChanges) {
        if (window.confirm(message)) {
          navigate(navigateTo);
        }
      } else {
        navigate(navigateTo);
      }
    },
    [hasUnsavedChanges, message, navigate, location.pathname]
  );
  
  return promptIfUnsaved;
}
